package com.hyperether.goodjob.scenes.scheduler

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.Divider
import androidx.compose.material3.DrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavHostController
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.log
import com.hyperether.goodjob.navigation.AddNew
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.scheduler
import com.hyperether.goodjob.scenes.components.FilterButton
import com.hyperether.goodjob.scenes.components.MobileHeaderSearchAdd
import com.hyperether.goodjob.scenes.jobs.components.FilterJobsDialog
import com.hyperether.goodjob.scenes.scheduler.components.SchedulerMonthJobCard
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.goodjob.util.JobStatusMap
import com.hyperether.planner.ui.DashboardDayPlanner
import com.hyperether.planner.ui.ScheduleMonthPlanner
import com.hyperether.planner.ui.SchedulerDayPlanner
import com.hyperether.planner.ui.WeekPlanner
import com.hyperether.resources.currentLanguage
import com.hyperether.resources.stringResource

@Composable
fun SchedulerScreen(
    viewModel: SchedulerViewModel,
    navHostController: NavHostController,
    drawerState: DrawerState,
    prefsManager: PrefsManager,
    isMobile: Boolean,
    screenWidth: Float = 0f,
    screenHeight: Float = 0f,
) {

    val periodSwitcherHeight = Dp(104f) //?
    val mobileHeaderSearchAddHeight = Dp(60f)
    val bottomNavigationHeight = Dp(56f) // BottomNavigationHeight

    viewModel.jobStatusMap = JobStatusMap()
    val events = viewModel.listOfPlans.flatMap { it.events }
    log.d("SchedulerScreen") { "events size: ${events.size}" }

    val contacts by viewModel.allContactsMap.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        FilterDialogStateManager.setDialogShowing(false)
        viewModel.jobClicked.value = false
        viewModel.loadRemoteData()
    }
    LaunchedEffect(viewModel.filteredJobs.toList(), viewModel.filteredJobs.size) {
        viewModel.parseSchedule()
        val events = viewModel.listOfPlans.flatMap { it.events }
        log.d("SchedulerScreen parseSchedule") { "parseSchedule: events size: ${events.size}" }
    }

    if (isMobile) {
        Column {
            MobileHeaderSearchAdd(
                title = stringResource(Res.string.scheduler),
                drawerState = drawerState,
                scope = rememberCoroutineScope(),
                add = { navHostController.navigate(AddNew) },
                search = {},
                removeSearch = true,
                user = prefsManager.getUser()
            )

            Row(
                modifier = Modifier.fillMaxWidth()
                    .padding(horizontal = Dp(20f), vertical = Dp(12f)),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier.weight(0.6f)
                ) {
                    PeriodChooser(viewModel.periodState) {
                        viewModel.periodChanged()
                        viewModel.parseSchedule()
                    }
                }
                if (viewModel.jobList.isNotEmpty()) {
                    FilterButton(
                        modifier = Modifier.align(Alignment.CenterVertically)
                    )
                }
            }

            Divider(
                modifier = Modifier.height(Dp(1f)).background(Color.LightGray.copy(alpha = 0.1f))
            )

            PeriodSwitcher(
                text = viewModel.currentPeriod.value,
                prev = {
                    viewModel.previous()
                    viewModel.periodChanged()
                    viewModel.parseSchedule()
                },
                next = {
                    viewModel.next()
                    viewModel.periodChanged()
                    viewModel.parseSchedule()
                },
                modifier = Modifier.fillMaxWidth()
            )

            if (viewModel.initialLoad.value) {
                viewModel.initialLoad.value = false
                viewModel.parseSchedule()
            }

            when (viewModel.periodState.value) {
                PeriodType.DAY -> {
                    if (!viewModel.isEmployee()) {
                        SchedulerDayPlanner(
                            listOfPlans = viewModel.listOfPlans,
                            startHour = viewModel.listOfPlans.flatMap { it.events }
                                .minOfOrNull { it.from.hour } ?: 8,
                            onEventClicked = { event ->
                                val selectedJob =
                                    viewModel.jobList.firstOrNull { it.id == event.jobId }
                                viewModel.selectedJobDashboard.value = selectedJob
                                selectedJob?.let { viewModel.updateSite(it) }
                                viewModel.jobClicked.value = true
                            },
                            screenHeight = if (screenHeight > 0) screenHeight - periodSwitcherHeight.value - mobileHeaderSearchAddHeight.value - bottomNavigationHeight.value else screenHeight,
                            screenWidth = screenWidth,
                            isMobile = isMobile
                        )
                    } else {
                        DashboardDayPlanner(
                            onEventClicked = { event ->
                                val selectedJob =
                                    viewModel.jobList.firstOrNull { it.id == event.jobId }
                                viewModel.selectedJobDashboard.value = selectedJob
                                selectedJob?.let { viewModel.updateSite(it) }
                                viewModel.jobClicked.value = true
                            },
                            listOfPlans = viewModel.listOfPlans,
                            screenHeight = if (screenHeight > 0) screenHeight - periodSwitcherHeight.value - mobileHeaderSearchAddHeight.value - bottomNavigationHeight.value else screenHeight,
                            screenWidth = screenWidth,
                            isMobileView = isMobile,
                            startHour = viewModel.listOfPlans.flatMap { it.events }
                                .minOfOrNull { it.from.hour } ?: 8)
                    }
                }

                PeriodType.WEEK ->
                    WeekPlanner(
                        listOfPlans = viewModel.listOfPlans,
                        weekStart = viewModel.weekSelectionStart.value,
                        weekEnd = viewModel.weekSelectionEnd.value,
                        onEventClicked = { event ->
                            val selectedJob =
                                viewModel.jobList.firstOrNull { it.id == event.jobId }
                            viewModel.selectedJobDashboard.value = selectedJob
                            selectedJob?.let { viewModel.updateSite(it) }
                            viewModel.jobClicked.value = true
                        },
                        onEventHovered = {
                            viewModel.hoveredEvent.value = it
                        },
                        isMobile = isMobile
                    )

                PeriodType.MONTH -> {
                    ScheduleMonthPlanner(
                        listOfPlans = viewModel.listOfPlans,
                        viewModel.monthSelectionLocalDate.value.monthNumber,
                        viewModel.monthSelectionLocalDate.value.year,
                        viewModel.monthSelectedDay,
                        language = currentLanguage.value.code
                    ) { list, day ->
                        viewModel.filterCurrentJobs(list)
                        viewModel.monthSelectedDay.value = day
                    }

                    LazyColumn {
                        items(viewModel.currentJobs.takeIf { it.isNotEmpty() }
                            ?: emptyList()) { job ->
                            SchedulerMonthJobCard(
                                status = job.status ?: "",
                                range = "${job.start_at} - ${job.end_at}",
                                title = job.jobTitle ?: "",
                                site = job.contactSiteName ?: "",
                                employee = job.assignees?.employees?.firstOrNull()?.getName() ?: "",
                                onClick = {
                                    val selectedJob =
                                        viewModel.jobList.firstOrNull { it.id == job.id }
                                    viewModel.selectedJobDashboard.value = selectedJob
                                    viewModel.jobClicked.value = true
                                }
                            )
                        }
                    }
                }
            }
        }

    } else {
        SelectionContainer {
            SchedulerScreenWeb(
                viewModel = viewModel,
                navController = navHostController,
                screenHeight = screenHeight,
                screenWidth = screenWidth
            )
        }
    }

    if (viewModel.jobClicked.value) {
        viewModel.hoveredEvent.value = null
        viewModel.selectedJobDashboard.value?.let {
            SchedulerJobWeeklyPopUpDetailed(
                viewModel.jobSites,
                contacts[it.id],
                job = it,
                onDismissRequest = {
                    viewModel.jobClicked.value = false
                }
            )
        }
    }

    val userTeams = viewModel.getUser()?.teams
    if (FilterDialogStateManager.dialogShowing.value) {
        FilterJobsDialog(
            viewModel.getFilterMap(),
            onDismiss = { FilterDialogStateManager.setDialogShowing(false) },
            onApply = { map ->
                FilterDialogStateManager.setDialogShowing(false)
                viewModel.applyFilter(map)
                viewModel.filterJobs()
                viewModel.parseSchedule()
            },
            isMobileView = isMobile,
            isSchedulerFilter = true,
            isEmployee = viewModel.isEmployee(),
            userTeams = userTeams
        )
    }

    if (!isMobile) {
        if (viewModel.hoveredEvent.value != null && viewModel.periodState.value == PeriodType.WEEK)
            viewModel.hoveredEvent.value?.let { event ->
//                log.d("SchedulerScreen") { "WeekPlaner hoveredEvent: $event" }
                val selectedJob = viewModel.jobList.firstOrNull { it.id == event.jobId }
//                log.d("SchedulerScreen") { "selectedJob: ${selectedJob}" }
                viewModel.selectedJobWeeklyHover.value = true
                viewModel.selectedJobDashboard.value = selectedJob
                selectedJob?.let {
                    viewModel.updateSite(it)
                    SchedulerJobHover(
                        viewModel.jobSites,
                        job = it,
                        viewModel.position.value.x,
                        viewModel.position.value.y
                    )
                }
            } ?: run {
                viewModel.selectedJobDashboard.value = null
                viewModel.selectedJobWeeklyHover.value = false
            }
    }
}